/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomTooltip } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiActions, uiSelectors, uiTypes } from '@opengeoweb/store';
import { Info } from '@opengeoweb/theme';
import { TimeSeriesTopRowButton } from '../TimeSeriesTopRowButton';

export interface LocationInfoButtonConnectProps {
  panelId: string;
  t: TFunction;
}

export const LocationInfoButtonConnect: React.FC<
  LocationInfoButtonConnectProps
> = ({ panelId, t }) => {
  const dispatch = useDispatch();

  const isOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(
      store,
      uiTypes.DialogTypes.TimeSeriesLocationInfo,
    ),
  );
  const currentSourcePanelId = useSelector((store: CoreAppStore) =>
    uiSelectors.getSourcePanelId(
      store,
      uiTypes.DialogTypes.TimeSeriesLocationInfo,
    ),
  );

  const handleClick = (): void => {
    dispatch(
      uiActions.setToggleOpenDialog({
        type: uiTypes.DialogTypes.TimeSeriesLocationInfo,
        setOpen: currentSourcePanelId !== panelId ? true : !isOpenInStore,
        sourcePanelId: panelId,
      }),
    );
  };

  const isOpenForPanel = isOpenInStore && currentSourcePanelId === panelId;

  return (
    <CustomTooltip title={t('timeseries-location-info-title')}>
      <span>
        <TimeSeriesTopRowButton
          data-testid="timeSeriesLocationInfoButton"
          icon={<Info />}
          longText={t('timeseries-location-info-button')}
          onClick={handleClick}
          selected={isOpenForPanel}
          variant="tool"
        />
      </span>
    </CustomTooltip>
  );
};
